import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Home, Volume2, VolumeX } from 'lucide-react';
import { IconButton, PrimaryButton, TertiaryButton } from '../components/brand/BrandButton';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import AudioTip from '../components/brand/AudioTip';

// Audio context for generating ambient sounds
const createAudioContext = () => {
    if (typeof window !== 'undefined') {
        return new (window.AudioContext || window.webkitAudioContext)();
    }
    return null;
};

/**
 * Still Sphere
 * 
 * A warm, grounding visual tool featuring a softly glowing sphere
 * that remains still and calm. No rhythm, no cycles, no entrainment -
 * just a quiet, steady presence to help the viewer settle.
 * 
 * Dynamic but non-rhythmic:
 * - Depth-drift layers at different ultra-slow speeds
 * - Subtle color-temperature drift (1-2% over 30-60 seconds)
 * - Micro-sway with unpredictable direction changes
 * - Soft focus shifts at imperceptible pace
 * - Background irregular deepening without rhythm
 */

// Visual Themes
const VISUAL_THEMES = [
    {
        id: 'golden',
        name: 'Golden Warmth',
        description: 'Warm, sunlit calm',
        baseColors: { r: 212, g: 175, b: 90 },
        bgGradient: 'from-[#2A2520] to-[#1A1815]',
        accentColor: '#E5D4A8',
        textColor: '#C5B5A0'
    },
    {
        id: 'ocean',
        name: 'Ocean Deep',
        description: 'Cool, peaceful depths',
        baseColors: { r: 100, g: 160, b: 200 },
        bgGradient: 'from-[#1A2530] to-[#0F1820]',
        accentColor: '#A8D4E5',
        textColor: '#A0B5C5'
    },
    {
        id: 'forest',
        name: 'Forest Glade',
        description: 'Natural, grounding green',
        baseColors: { r: 120, g: 180, b: 130 },
        bgGradient: 'from-[#1A2520] to-[#101815]',
        accentColor: '#B8E5C8',
        textColor: '#A5C5B0'
    },
    {
        id: 'twilight',
        name: 'Twilight',
        description: 'Soft purple serenity',
        baseColors: { r: 160, g: 130, b: 190 },
        bgGradient: 'from-[#25202A] to-[#18151A]',
        accentColor: '#D4C8E5',
        textColor: '#B5A5C5'
    },
    {
        id: 'rose',
        name: 'Rose Quartz',
        description: 'Gentle, heart-centered pink',
        baseColors: { r: 200, g: 150, b: 160 },
        bgGradient: 'from-[#2A2025] to-[#1A1518]',
        accentColor: '#E5D0D8',
        textColor: '#C5B0B5'
    },
    {
        id: 'moonlight',
        name: 'Moonlight',
        description: 'Cool silver tranquility',
        baseColors: { r: 180, g: 185, b: 195 },
        bgGradient: 'from-[#202428] to-[#15181A]',
        accentColor: '#E0E4E8',
        textColor: '#B5B8C0'
    }
];

// Session duration in seconds (2 minutes 30 seconds)
const SESSION_DURATION = 150;

// The Still Sphere with atmospheric, non-rhythmic dynamics
const StillSphereOrb = ({ swayOffset, warmthShift, focusShift, hazeDepths, theme }) => {
    // Color temperature shift (1-2% warmth variation)
    const baseR = theme.baseColors.r;
    const baseG = theme.baseColors.g;
    const baseB = theme.baseColors.b;
    
    const warmthR = baseR + warmthShift * 8;
    const warmthG = baseG + warmthShift * 4;
    const warmthB = baseB - warmthShift * 3;

    // Focus shift creates soft blur variation
    const blurAmount = 50 + focusShift * 8;

    return (
        <motion.div
            className="relative flex items-center justify-center"
            animate={{
                x: swayOffset.x,
                y: swayOffset.y,
            }}
            transition={{ 
                duration: 8 + Math.random() * 4,
                ease: "easeInOut" 
            }}
        >
            {/* Outer atmospheric glow - warmth shifts subtly */}
            <div
                className="absolute rounded-full"
                style={{
                    width: `min(85vw, 480px)`,
                    height: `min(85vw, 480px)`,
                    background: `radial-gradient(circle, 
                        rgba(${warmthR}, ${warmthG}, ${warmthB}, 0.08) 0%, 
                        rgba(${warmthR - 17}, ${warmthG - 15}, ${warmthB - 10}, 0.04) 50%, 
                        transparent 75%
                    )`,
                    filter: `blur(${blurAmount}px)`,
                    transition: 'filter 12s ease-in-out, background 25s ease-in-out',
                }}
            />

            {/* Main sphere body */}
            <div
                className="relative rounded-full overflow-hidden"
                style={{
                    width: `min(60vw, 320px)`,
                    height: `min(60vw, 320px)`,
                    boxShadow: `
                        0 0 ${80 + focusShift * 10}px rgba(${warmthR + 23}, ${warmthG + 30}, ${warmthB + 40}, 0.25),
                        0 0 ${150 + focusShift * 15}px rgba(${warmthR}, ${warmthG}, ${warmthB}, 0.12),
                        0 0 ${220 + focusShift * 20}px rgba(${warmthR - 17}, ${warmthG - 15}, ${warmthB - 10}, 0.06),
                        inset 0 0 100px rgba(255, 250, 235, 0.06)
                    `,
                    transition: 'box-shadow 15s ease-in-out',
                }}
            >
                {/* Base golden fill - warmth shifts */}
                <div 
                    className="absolute inset-0 rounded-full"
                    style={{
                        background: `radial-gradient(circle at 48% 46%, 
                            rgba(255, ${248 + warmthShift * 2}, ${220 - warmthShift * 5}, 0.65) 0%, 
                            rgba(${245 + warmthShift * 3}, ${218 + warmthShift * 2}, ${140 - warmthShift * 8}, 0.55) 25%, 
                            rgba(${225 + warmthShift * 4}, ${190 + warmthShift * 2}, ${110 - warmthShift * 5}, 0.45) 50%, 
                            rgba(${205 + warmthShift * 3}, ${170 + warmthShift * 2}, ${95 - warmthShift * 4}, 0.35) 75%,
                            rgba(${185 + warmthShift * 2}, ${150 + warmthShift}, ${80 - warmthShift * 3}, 0.25) 100%
                        )`,
                        transition: 'background 30s ease-in-out',
                    }}
                />

                {/* Internal haze layer 1 - static position, no rotation */}
                <div
                    className="absolute inset-[-25%] rounded-full"
                    style={{
                        background: `
                            radial-gradient(ellipse 75% 55% at ${48 + hazeDepths.layer1.x * 0.5}% ${47 + hazeDepths.layer1.y * 0.4}%, rgba(255, 252, 230, 0.35) 0%, transparent 55%),
                            radial-gradient(ellipse 55% 75% at ${52 - hazeDepths.layer1.x * 0.3}% ${53 + hazeDepths.layer1.y * 0.35}%, rgba(255, 245, 210, 0.28) 0%, transparent 50%),
                            radial-gradient(ellipse 65% 45% at ${50 + hazeDepths.layer1.y * 0.25}% ${50 - hazeDepths.layer1.x * 0.2}%, rgba(250, 230, 180, 0.22) 0%, transparent 55%)
                        `,
                        filter: 'blur(18px)',
                        transition: 'background 40s ease-in-out',
                    }}
                />

                {/* Internal haze layer 2 - different drift phase */}
                <div
                    className="absolute inset-[-20%] rounded-full"
                    style={{
                        background: `
                            radial-gradient(ellipse 85% 65% at ${50 - hazeDepths.layer2.x * 0.4}% ${50 + hazeDepths.layer2.y * 0.3}%, rgba(255, 250, 225, 0.3) 0%, transparent 50%),
                            radial-gradient(ellipse 50% 85% at ${50 + hazeDepths.layer2.x * 0.3}% ${50 - hazeDepths.layer2.y * 0.25}%, rgba(252, 240, 195, 0.24) 0%, transparent 48%)
                        `,
                        filter: 'blur(15px)',
                        transition: 'background 45s ease-in-out',
                    }}
                />

                {/* Internal haze layer 3 - deepest, slowest */}
                <div
                    className="absolute inset-[-15%] rounded-full"
                    style={{
                        background: `
                            radial-gradient(ellipse 95% 75% at ${50 + hazeDepths.layer3.x * 0.25}% ${50 - hazeDepths.layer3.y * 0.2}%, rgba(255, 253, 240, 0.2) 0%, transparent 45%),
                            radial-gradient(ellipse 65% 95% at ${50 - hazeDepths.layer3.x * 0.2}% ${50 + hazeDepths.layer3.y * 0.2}%, rgba(250, 242, 210, 0.15) 0%, transparent 42%)
                        `,
                        filter: 'blur(22px)',
                        transition: 'background 50s ease-in-out',
                    }}
                />

                {/* Infinite depth illusion - darker center that recedes */}
                <div 
                    className="absolute inset-0 rounded-full"
                    style={{
                        background: `radial-gradient(circle at 50% 50%, 
                            transparent 0%, 
                            transparent 35%,
                            rgba(${185 + warmthShift * 2}, ${155 + warmthShift}, ${85 - warmthShift}, 0.04) 60%,
                            rgba(${165 + warmthShift * 2}, ${140 + warmthShift}, ${75 - warmthShift}, 0.07) 80%,
                            rgba(${145 + warmthShift}, ${125 + warmthShift * 0.5}, ${65 - warmthShift * 0.5}, 0.1) 100%
                        )`,
                        transition: 'background 20s ease-in-out',
                    }}
                />

                {/* Subtle inner luminosity - static, warm */}
                <div
                    className="absolute inset-0 rounded-full"
                    style={{
                        background: `radial-gradient(circle at 44% 44%, 
                            rgba(255, 255, ${248 + warmthShift * 2}, ${0.08 + focusShift * 0.02}) 0%, 
                            transparent 40%
                        )`,
                        transition: 'background 18s ease-in-out',
                    }}
                />

                {/* Soft edge dissolution with focus shift */}
                <div 
                    className="absolute inset-0 rounded-full"
                    style={{
                        background: `radial-gradient(circle, 
                            transparent ${72 - focusShift * 2}%, 
                            rgba(42, 38, 32, ${0.12 + focusShift * 0.02}) 88%,
                            rgba(42, 38, 32, ${0.25 + focusShift * 0.03}) 100%
                        )`,
                        transition: 'background 15s ease-in-out',
                    }}
                />
            </div>
        </motion.div>
    );
};

// Atmospheric background - ultra-slow, directionless warmth
// Movement is below conscious tracking threshold
const WarmHazeBackground = ({ atmosphericState, warmthShift, theme }) => {
    // Very dark warm base using theme colors
    const baseR = (theme.baseColors.r * 0.08) + warmthShift * 0.8;
    const baseG = (theme.baseColors.g * 0.08) + warmthShift * 0.4;
    const baseB = (theme.baseColors.b * 0.08) - warmthShift * 0.2;

    return (
        <div className="fixed inset-0 overflow-hidden">
            {/* Solid dark warm base */}
            <div 
                className="absolute inset-0"
                style={{
                    background: `rgb(${Math.max(12, baseR)}, ${Math.max(10, baseG)}, ${Math.max(8, baseB)})`,
                    transition: 'background 60s ease-in-out',
                }}
            />

            {/* Central atmospheric warmth - static, no movement */}
            <div
                className="absolute inset-0"
                style={{
                    background: `radial-gradient(ellipse 140% 120% at 50% 50%, 
                        rgba(${210 + warmthShift * 2}, ${172 + warmthShift}, ${88 - warmthShift}, ${0.055 + atmosphericState.depth * 0.015}) 0%, 
                        rgba(${195 + warmthShift}, ${158 + warmthShift * 0.5}, ${78 - warmthShift * 0.5}, ${0.032 + atmosphericState.depth * 0.008}) 45%,
                        transparent 80%
                    )`,
                    transition: 'background 45s ease-in-out',
                }}
            />

            {/* Deep atmospheric layer 1 - imperceptible drift */}
            <div
                className="absolute inset-[-15%]"
                style={{
                    background: `
                        radial-gradient(ellipse 70% 55% at ${48 + atmosphericState.drift1 * 0.3}% ${47 + atmosphericState.drift2 * 0.25}%, 
                            rgba(${208 + warmthShift}, ${170 + warmthShift * 0.5}, 92, ${0.028 + atmosphericState.depth * 0.008}) 0%, transparent 60%),
                        radial-gradient(ellipse 55% 65% at ${52 - atmosphericState.drift2 * 0.2}% ${53 - atmosphericState.drift1 * 0.2}%, 
                            rgba(${202 + warmthShift}, ${165 + warmthShift * 0.5}, 86, ${0.022 + atmosphericState.depth * 0.006}) 0%, transparent 55%)
                    `,
                    filter: 'blur(90px)',
                    transition: 'background 50s ease-in-out',
                }}
            />

            {/* Deep atmospheric layer 2 - different phase, even slower */}
            <div
                className="absolute inset-[-10%]"
                style={{
                    background: `
                        radial-gradient(ellipse 60% 50% at ${50 + atmosphericState.drift3 * 0.2}% ${50 + atmosphericState.drift4 * 0.15}%, 
                            rgba(${205 + warmthShift}, ${168 + warmthShift * 0.5}, 90, ${0.018 + atmosphericState.depth * 0.005}) 0%, transparent 55%),
                        radial-gradient(ellipse 50% 60% at ${50 - atmosphericState.drift4 * 0.15}% ${50 - atmosphericState.drift3 * 0.18}%, 
                            rgba(${200 + warmthShift}, ${163 + warmthShift * 0.5}, 85, ${0.015 + atmosphericState.depth * 0.004}) 0%, transparent 50%)
                    `,
                    filter: 'blur(110px)',
                    transition: 'background 60s ease-in-out',
                }}
            />

            {/* Soft warm envelope - no hard vignette */}
            <div 
                className="absolute inset-0"
                style={{
                    background: `radial-gradient(circle at 50% 50%, 
                        transparent 55%, 
                        rgba(${28 + warmthShift * 0.3}, ${25 + warmthShift * 0.2}, ${22 - warmthShift * 0.1}, ${0.12 + atmosphericState.depth * 0.02}) 85%,
                        rgba(${22 + warmthShift * 0.2}, ${20 + warmthShift * 0.1}, ${18}, ${0.22 + atmosphericState.depth * 0.03}) 100%
                    )`,
                    transition: 'background 40s ease-in-out',
                }}
            />
        </div>
    );
};

const StillSpherePage = () => {
    const navigate = useNavigate();
    const [phase, setPhase] = useState('intro');
    const [isActive, setIsActive] = useState(false);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [selectedTheme, setSelectedTheme] = useState('golden');
    const [showThemeSelector, setShowThemeSelector] = useState(false);
    
    // Audio state - OFF by default for ADHD sensory sensitivity
    const [soundEnabled, setSoundEnabled] = useState(false);
    const audioContextRef = useRef(null);
    const noiseNodeRef = useRef(null);
    const filterNodeRef = useRef(null);
    const gainNodeRef = useRef(null);
    const lfoRef = useRef(null);
    const lfoGainRef = useRef(null);
    
    const theme = VISUAL_THEMES.find(t => t.id === selectedTheme) || VISUAL_THEMES[0];
    
    // Non-rhythmic dynamic states
    const [swayOffset, setSwayOffset] = useState({ x: 0, y: 0 });
    const [warmthShift, setWarmthShift] = useState(0); // -1 to 1 (cooler to warmer)
    const [focusShift, setFocusShift] = useState(0); // -1 to 1 (sharper to softer)
    
    // Atmospheric background state - ultra-slow, imperceptible drift
    const [atmosphericState, setAtmosphericState] = useState({
        depth: 0,
        drift1: 0,
        drift2: 0,
        drift3: 0,
        drift4: 0
    });
    
    // Orb internal haze - slow gradient position shifts
    const [hazeDepths, setHazeDepths] = useState({
        layer1: { x: 0, y: 0 },
        layer2: { x: 0, y: 0 },
        layer3: { x: 0, y: 0 }
    });
    
    const startTimeRef = useRef(null);
    const warmthTargetRef = useRef(0);
    const focusTargetRef = useRef(0);
    const atmosphericTimeRef = useRef(0);
    const hazeTimeRef = useRef(0);
    
    // Initialize and create ambient soundscape
    const initAudio = useCallback(() => {
        if (!audioContextRef.current) {
            audioContextRef.current = createAudioContext();
        }
        const ctx = audioContextRef.current;
        if (!ctx) return;
        
        if (ctx.state === 'suspended') {
            ctx.resume();
        }
        
        // Create brown noise (filtered white noise) for ambient warmth
        if (!noiseNodeRef.current) {
            // Create noise buffer
            const bufferSize = 2 * ctx.sampleRate;
            const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
            const output = noiseBuffer.getChannelData(0);
            
            // Generate brown noise (random walk)
            let lastOut = 0;
            for (let i = 0; i < bufferSize; i++) {
                const white = Math.random() * 2 - 1;
                output[i] = (lastOut + (0.02 * white)) / 1.02;
                lastOut = output[i];
                output[i] *= 3.5; // Boost volume
            }
            
            // Create noise source
            noiseNodeRef.current = ctx.createBufferSource();
            noiseNodeRef.current.buffer = noiseBuffer;
            noiseNodeRef.current.loop = true;
            
            // Low-pass filter for warmth
            filterNodeRef.current = ctx.createBiquadFilter();
            filterNodeRef.current.type = 'lowpass';
            filterNodeRef.current.frequency.setValueAtTime(200, ctx.currentTime);
            filterNodeRef.current.Q.setValueAtTime(1, ctx.currentTime);
            
            // Very subtle LFO for gentle movement (below conscious perception)
            lfoRef.current = ctx.createOscillator();
            lfoGainRef.current = ctx.createGain();
            lfoRef.current.frequency.setValueAtTime(0.05, ctx.currentTime); // Very slow modulation
            lfoGainRef.current.gain.setValueAtTime(30, ctx.currentTime);
            
            lfoRef.current.connect(lfoGainRef.current);
            lfoGainRef.current.connect(filterNodeRef.current.frequency);
            
            // Main gain node
            gainNodeRef.current = ctx.createGain();
            gainNodeRef.current.gain.setValueAtTime(0, ctx.currentTime);
            
            // Connect: noise -> filter -> gain -> output
            noiseNodeRef.current.connect(filterNodeRef.current);
            filterNodeRef.current.connect(gainNodeRef.current);
            gainNodeRef.current.connect(ctx.destination);
            
            // Start
            noiseNodeRef.current.start();
            lfoRef.current.start();
        }
        
        // Fade in gently
        if (gainNodeRef.current) {
            gainNodeRef.current.gain.setTargetAtTime(0.06, ctx.currentTime, 1.5);
        }
    }, []);
    
    // Stop audio with fade out
    const stopAudio = useCallback(() => {
        if (gainNodeRef.current && audioContextRef.current) {
            gainNodeRef.current.gain.setTargetAtTime(0, audioContextRef.current.currentTime, 0.5);
        }
        setTimeout(() => {
            if (noiseNodeRef.current) {
                try {
                    noiseNodeRef.current.stop();
                } catch (e) { /* already stopped */ }
                noiseNodeRef.current = null;
            }
            if (lfoRef.current) {
                try {
                    lfoRef.current.stop();
                } catch (e) { /* already stopped */ }
                lfoRef.current = null;
            }
        }, 800);
    }, []);
    
    // Toggle sound
    const toggleSound = useCallback(() => {
        if (!soundEnabled) {
            initAudio();
            setSoundEnabled(true);
        } else {
            stopAudio();
            setSoundEnabled(false);
        }
    }, [soundEnabled, initAudio, stopAudio]);
    
    // Clean up audio on unmount
    useEffect(() => {
        return () => {
            stopAudio();
            if (audioContextRef.current) {
                audioContextRef.current.close();
                audioContextRef.current = null;
            }
        };
    }, [stopAudio]);

    // Micro-sway with unpredictable direction changes - very subtle
    useEffect(() => {
        if (!isActive) return;

        let swayTime = 0;
        const sway = () => {
            swayTime += 0.0008; // Extremely slow progression
            
            // Multi-frequency drift that never repeats perceptibly
            // Each component uses prime-related frequencies to avoid pattern
            setSwayOffset({
                x: Math.sin(swayTime * 1.1) * 1.2 + Math.sin(swayTime * 0.37) * 0.8 + Math.sin(swayTime * 0.13) * 0.4,
                y: Math.cos(swayTime * 0.97) * 1.0 + Math.cos(swayTime * 0.31) * 0.6 + Math.cos(swayTime * 0.11) * 0.35
            });
        };

        const interval = setInterval(sway, 200);
        return () => clearInterval(interval);
    }, [isActive]);

    // Color-temperature drift (1-2% shift over 45-90 seconds)
    useEffect(() => {
        if (!isActive) return;

        const warmthDrift = () => {
            // Very rarely set new target (average every 60+ seconds)
            if (Math.random() < 0.008) {
                warmthTargetRef.current = (Math.random() - 0.5) * 1.5; // -0.75 to 0.75, subtle range
            }
            
            // Extremely slow interpolation toward target
            setWarmthShift(prev => {
                const diff = warmthTargetRef.current - prev;
                return prev + diff * 0.008; // Imperceptibly slow
            });
        };

        const interval = setInterval(warmthDrift, 1500);
        return () => clearInterval(interval);
    }, [isActive]);

    // Soft focus shifts at imperceptible pace
    useEffect(() => {
        if (!isActive) return;

        const focusDrift = () => {
            // Even more rarely set new target
            if (Math.random() < 0.006) {
                focusTargetRef.current = (Math.random() - 0.5) * 1.2;
            }
            
            setFocusShift(prev => {
                const diff = focusTargetRef.current - prev;
                return prev + diff * 0.006;
            });
        };

        const interval = setInterval(focusDrift, 2000);
        return () => clearInterval(interval);
    }, [isActive]);

    // Atmospheric background drift - below conscious tracking threshold
    useEffect(() => {
        if (!isActive) return;

        const atmosphericDrift = () => {
            atmosphericTimeRef.current += 0.0003; // Glacially slow
            const t = atmosphericTimeRef.current;
            
            // Each drift uses incommensurate frequencies - never loops perceptibly
            // Movement is centered around 50%, shifts only 1-3% in any direction
            setAtmosphericState(prev => ({
                depth: prev.depth + ((Math.random() - 0.5) * 0.003), // Tiny random walk
                drift1: Math.sin(t * 0.7) * 1.5 + Math.sin(t * 0.23) * 0.8,
                drift2: Math.cos(t * 0.53) * 1.2 + Math.cos(t * 0.17) * 0.7,
                drift3: Math.sin(t * 0.41 + 1.2) * 1.0 + Math.sin(t * 0.13 + 0.5) * 0.6,
                drift4: Math.cos(t * 0.31 + 0.8) * 0.9 + Math.cos(t * 0.11 + 1.1) * 0.5
            }));
        };

        const interval = setInterval(atmosphericDrift, 500);
        return () => clearInterval(interval);
    }, [isActive]);

    // Orb internal haze layers - different ultra-slow phases
    useEffect(() => {
        if (!isActive) return;

        const updateLayers = () => {
            hazeTimeRef.current += 0.0002; // Even slower than background
            const t = hazeTimeRef.current;
            
            // Each layer uses unique frequency combinations
            // Total drift is only 2-4 units, centered at zero
            setHazeDepths({
                layer1: {
                    x: Math.sin(t * 0.83) * 2 + Math.sin(t * 0.29) * 1.2,
                    y: Math.cos(t * 0.71) * 1.8 + Math.cos(t * 0.23) * 1
                },
                layer2: {
                    x: Math.sin(t * 0.61 + 0.7) * 1.6 + Math.sin(t * 0.19 + 0.3) * 1,
                    y: Math.cos(t * 0.53 + 1.1) * 1.4 + Math.cos(t * 0.17 + 0.6) * 0.8
                },
                layer3: {
                    x: Math.sin(t * 0.43 + 1.4) * 1.3 + Math.sin(t * 0.13 + 0.9) * 0.8,
                    y: Math.cos(t * 0.37 + 1.8) * 1.1 + Math.cos(t * 0.11 + 1.2) * 0.6
                }
            });
        };

        const interval = setInterval(updateLayers, 600);
        return () => clearInterval(interval);
    }, [isActive]);

    // Session timer
    useEffect(() => {
        if (phase !== 'session' || !isActive) return;

        if (!startTimeRef.current) {
            startTimeRef.current = Date.now() - (elapsedTime * 1000);
        }

        const timer = setInterval(() => {
            const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
            setElapsedTime(elapsed);
            
            if (elapsed >= SESSION_DURATION) {
                setPhase('complete');
                setIsActive(false);
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [phase, isActive, elapsedTime]);

    const handleBegin = () => {
        setPhase('session');
        setIsActive(true);
        startTimeRef.current = Date.now();
        if (soundEnabled) {
            initAudio();
        }
    };

    const handleFinishEarly = () => {
        setPhase('complete');
        setIsActive(false);
        stopAudio();
    };

    const handleRestart = () => {
        setPhase('intro');
        setIsActive(false);
        setElapsedTime(0);
        startTimeRef.current = null;
        stopAudio();
    };

    const remainingTime = Math.max(0, SESSION_DURATION - elapsedTime);
    const minutes = Math.floor(remainingTime / 60);
    const seconds = remainingTime % 60;

    return (
        <div className="min-h-screen flex flex-col relative overflow-hidden">
            {/* Warm haze background with ultra-slow atmospheric drift */}
            <WarmHazeBackground 
                atmosphericState={atmosphericState}
                warmthShift={warmthShift}
                theme={theme}
            />

            {/* Header */}
            <header className="sticky top-0 z-50 bg-transparent">
                <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
                    <IconButton 
                        onClick={() => navigate('/home?tab=regulation')}
                        className="text-[#7D8E95]/50 hover:text-[#FFBB98] hover:bg-[#1D6B6B]/40 backdrop-blur-sm"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </IconButton>
                    <SmallText className="text-[#B5A590]/80 font-medium">Still Sphere</SmallText>
                    <IconButton 
                        onClick={() => navigate('/home?tab=regulation')}
                        className="text-[#7D8E95]/50 hover:text-[#FFBB98] hover:bg-[#1D6B6B]/40 backdrop-blur-sm"
                    >
                        <Home className="w-5 h-5" />
                    </IconButton>
                </div>
            </header>

            {/* Main content */}
            <main className="flex-1 flex flex-col items-center justify-center px-4 py-8 relative z-10">
                {/* Intro content */}
                <AnimatePresence>
                    {phase === 'intro' && (
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 1 }}
                            className="text-center px-6 max-w-md mb-8"
                        >
                            <Heading as="h2" size="text-2xl" className="mb-4" style={{ color: theme.accentColor, opacity: 0.85 }}>
                                Still Sphere
                            </Heading>
                            <BodyText className="mb-4 leading-relaxed" style={{ color: theme.textColor, opacity: 0.7 }}>
                                A warm, quiet presence to help you settle.
                            </BodyText>
                            <SmallText className="block leading-relaxed" style={{ color: theme.textColor, opacity: 0.6 }}>
                                Simply rest your gaze on the sphere. There's no rhythm to follow, 
                                nothing to do. Just let the stillness meet you where you are.
                            </SmallText>
                            
                            {/* Audio tip */}
                            <div className="mt-4">
                                <AudioTip variant="ambient" />
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Timer display during session */}
                {phase === 'session' && (
                    <motion.div 
                        className="mb-4 flex flex-col items-center"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1, duration: 1 }}
                    >
                        <SmallText className="text-[#7D8E95]/50">
                            {minutes}:{seconds.toString().padStart(2, '0')}
                        </SmallText>
                    </motion.div>
                )}

                {/* The Sphere with atmospheric dynamics */}
                <div className="relative flex items-center justify-center" style={{ minHeight: phase === 'intro' ? '280px' : '380px' }}>
                    <StillSphereOrb 
                        swayOffset={swayOffset}
                        warmthShift={warmthShift}
                        focusShift={focusShift}
                        hazeDepths={hazeDepths}
                        theme={theme}
                    />

                    {/* Completion overlay */}
                    <AnimatePresence>
                        {phase === 'complete' && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 2 }}
                                className="absolute inset-0 flex items-center justify-center"
                            >
                                <motion.div 
                                    className="text-center px-6 max-w-sm"
                                    initial={{ scale: 0.95, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ delay: 0.8, duration: 1.2 }}
                                >
                                    <Heading as="h2" size="text-xl" className="mb-4" style={{ color: theme.accentColor, opacity: 0.85 }}>
                                        You are still.
                                    </Heading>
                                    <BodyText className="mb-10" style={{ color: theme.textColor, opacity: 0.6 }}>
                                        Carry this quietness with you.
                                    </BodyText>
                                    <div className="space-y-4">
                                        <PrimaryButton 
                                            onClick={() => navigate('/home?tab=regulation')}
                                            className="w-full border-0"
                                            style={{ 
                                                backgroundColor: `rgba(${theme.baseColors.r}, ${theme.baseColors.g}, ${theme.baseColors.b}, 0.8)`,
                                                color: '#2A2620'
                                            }}
                                        >
                                            Return Home
                                        </PrimaryButton>
                                        <button 
                                            onClick={handleRestart}
                                            className="text-sm transition-colors"
                                            style={{ color: theme.textColor, opacity: 0.5 }}
                                        >
                                            Stay longer
                                        </button>
                                    </div>
                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Session Controls - Central location below sphere */}
                {phase === 'session' && (
                    <motion.div 
                        className="flex flex-col items-center mt-8 gap-6"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.5 }}
                    >
                        {/* Main Controls Row */}
                        <div className="flex items-center gap-6">
                            {/* Color Theme Button */}
                            <div className="flex flex-col items-center gap-1">
                                <button
                                    onClick={() => setShowThemeSelector(!showThemeSelector)}
                                    className="w-12 h-12 rounded-full bg-[#2A2520]/60 backdrop-blur-sm border border-[#3A3530]/50 hover:border-white/20 transition-all duration-300 flex items-center justify-center"
                                    data-testid="theme-toggle-btn"
                                >
                                    <div 
                                        className="w-6 h-6 rounded-full"
                                        style={{ 
                                            backgroundColor: `rgb(${theme.baseColors.r}, ${theme.baseColors.g}, ${theme.baseColors.b})`,
                                            boxShadow: `0 0 12px rgba(${theme.baseColors.r}, ${theme.baseColors.g}, ${theme.baseColors.b}, 0.5)`
                                        }}
                                    />
                                </button>
                                <SmallText className="text-[#7D8E95]/40 text-xs">Color</SmallText>
                            </div>

                            {/* Sound Toggle */}
                            <div className="flex flex-col items-center gap-1">
                                <button
                                    onClick={toggleSound}
                                    className={`w-14 h-14 rounded-full backdrop-blur-sm border transition-all duration-300 flex items-center justify-center ${
                                        soundEnabled 
                                            ? 'bg-[#FFBB98]/20 border-[#FFBB98]/40' 
                                            : 'bg-[#2A2520]/60 border-[#3A3530]/50 hover:border-white/20'
                                    }`}
                                    data-testid="sound-toggle-btn"
                                >
                                    {soundEnabled ? (
                                        <Volume2 className="w-6 h-6 text-[#FFBB98]" />
                                    ) : (
                                        <VolumeX className="w-6 h-6 text-[#7D8E95]/60" />
                                    )}
                                </button>
                                <SmallText className={`text-xs ${soundEnabled ? 'text-[#FFBB98]/70' : 'text-[#7D8E95]/40'}`}>
                                    {soundEnabled ? 'Sound On' : 'Sound Off'}
                                </SmallText>
                            </div>
                        </div>

                        {/* Theme Selector Popup */}
                        <AnimatePresence>
                            {showThemeSelector && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 10 }}
                                    className="p-3 rounded-xl backdrop-blur-md"
                                    style={{ backgroundColor: 'rgba(30,28,25,0.95)' }}
                                >
                                    <SmallText className="text-[#7D8E95]/60 text-xs mb-2 block text-center">Choose a color theme</SmallText>
                                    <div className="grid grid-cols-3 gap-2">
                                        {VISUAL_THEMES.map(t => (
                                            <button
                                                key={t.id}
                                                onClick={() => {
                                                    setSelectedTheme(t.id);
                                                    setShowThemeSelector(false);
                                                }}
                                                className={`p-2 rounded-lg flex flex-col items-center gap-1 transition-all ${
                                                    selectedTheme === t.id ? 'ring-1 ring-white/30 bg-white/10' : 'hover:bg-white/5'
                                                }`}
                                            >
                                                <div 
                                                    className="w-6 h-6 rounded-full"
                                                    style={{ 
                                                        backgroundColor: `rgb(${t.baseColors.r}, ${t.baseColors.g}, ${t.baseColors.b})`,
                                                        boxShadow: selectedTheme === t.id ? `0 0 10px rgba(${t.baseColors.r}, ${t.baseColors.g}, ${t.baseColors.b}, 0.6)` : 'none'
                                                    }}
                                                />
                                                <span className="text-xs" style={{ color: t.textColor }}>
                                                    {t.name.split(' ')[0]}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* I'm done button */}
                        <button 
                            onClick={handleFinishEarly}
                            className="text-sm transition-colors"
                            style={{ color: theme.textColor, opacity: 0.4 }}
                            onMouseEnter={(e) => e.target.style.opacity = '0.7'}
                            onMouseLeave={(e) => e.target.style.opacity = '0.4'}
                            data-testid="finish-early-btn"
                        >
                            I'm done
                        </button>
                    </motion.div>
                )}

                {/* Intro buttons */}
                <AnimatePresence>
                    {phase === 'intro' && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 20 }}
                            transition={{ duration: 1, delay: 0.4 }}
                            className="text-center mt-8 px-6 max-w-sm"
                        >
                            <div className="space-y-4">
                                <PrimaryButton 
                                    onClick={handleBegin}
                                    className="w-full bg-[#C5A868]/80 hover:bg-[#D4B878] text-[#2A2620] border-0"
                                >
                                    Begin
                                </PrimaryButton>
                                <TertiaryButton 
                                    onClick={() => navigate('/home?tab=regulation')}
                                    className="w-full text-[#7D8E95]/60 hover:text-[#C5B5A0]"
                                >
                                    Not Now
                                </TertiaryButton>
                            </div>
                            
                            <SmallText className="text-[#807568]/50 mt-6 text-xs block">
                                ~2.5 minutes
                            </SmallText>
                        </motion.div>
                    )}
                </AnimatePresence>
            </main>
        </div>
    );
};

export default StillSpherePage;
